/**
 * Star Line commands for a TSP100 left in Star Line mode.
 * Image uses the Star Line 24-dot graphic command. Text uses Star Line
 * characters, Windows-1252 (ESC GS t 32), and the Star QR command.
 * The bytes go out through the same network and Windows shared ports.
 */

const BASE_COLUMNS = 48
const ESC = 0x1b
const GS = 0x1d

export function encodeWindows1252(text: string): number[] {
    const bytes: number[] = []
    for (const ch of text) {
        const code = ch.codePointAt(0) ?? 0x3f
        if (code === 0x20ac) {
            bytes.push(0x80)
            continue
        }
        if (code === 0x0153) {
            bytes.push(0x6f, 0x65)
            continue
        }
        if (code === 0x0152) {
            bytes.push(0x4f, 0x45)
            continue
        }
        if (code <= 0x7f || (code >= 0xa0 && code <= 0xff)) {
            bytes.push(code)
            continue
        }
        const folded = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        for (const part of folded || '?') {
            const ascii = part.codePointAt(0) ?? 0x3f
            bytes.push(ascii <= 0x7f ? ascii : 0x3f)
        }
    }
    return bytes
}

function clampSize(value: number): number {
    if (!Number.isFinite(value)) return 1
    return Math.min(6, Math.max(1, Math.round(value)))
}

export function columnsFor(width: number): number {
    const full = Math.max(16, Math.floor(BASE_COLUMNS / clampSize(width)))
    return Math.max(16, full - 2)
}

function wrapWords(text: string, maxWidth: number): string[] {
    const clean = String(text || '').replace(/\s+/g, ' ').trim()
    if (!clean) return ['']
    if (maxWidth < 1) return [clean]
    const lines: string[] = []
    let current = ''
    for (const word of clean.split(' ')) {
        if (word.length > maxWidth) {
            if (current) {
                lines.push(current)
                current = ''
            }
            for (let i = 0; i < word.length; i += maxWidth) lines.push(word.slice(i, i + maxWidth))
            continue
        }
        const next = current ? `${current} ${word}` : word
        if (next.length <= maxWidth) current = next
        else {
            if (current) lines.push(current)
            current = word
        }
    }
    if (current) lines.push(current)
    return lines.length ? lines : ['']
}

export class StarLineText {
    private bytes: number[] = []
    private align = -1
    private size = -1
    private qrSize: number
    private qrError: number
    private feedBeforeCut: number

    constructor(emptyLines = 0, options?: { qrSize?: number; qrError?: number; feedBeforeCut?: number }) {
        this.qrSize = Math.min(8, Math.max(1, options?.qrSize || 5))
        this.qrError = options?.qrError ?? 0x01
        this.feedBeforeCut = Math.max(1, options?.feedBeforeCut || 3)
        this.bytes.push(ESC, 0x40, 0x18)
        this.bytes.push(ESC, GS, 0x74, 32)
        this.bytes.push(ESC, 0x45)
        for (let i = 0; i < Math.max(1, emptyLines); i++) this.bytes.push(0x0a, 0x0d)
    }

    private ensure(align: number, width: number, height: number) {
        if (this.align !== align) {
            this.bytes.push(ESC, GS, 0x61, align)
            this.align = align
        }
        const nextWidth = clampSize(width)
        const nextHeight = clampSize(height)
        const nextSize = (nextWidth << 8) | nextHeight
        if (this.size !== nextSize) {
            this.bytes.push(ESC, 0x69, nextHeight - 1, nextWidth - 1)
            this.bytes.push(ESC, 0x45)
            this.size = nextSize
        }
    }

    line(text: string, align: number, width: number, height: number) {
        this.ensure(align, width, height)
        const limit = columnsFor(width)
        const safe = text.length > limit ? text.slice(0, limit) : text
        this.bytes.push(...encodeWindows1252(safe))
        this.bytes.push(0x0a, 0x0d)
    }

    boldLine(text: string, align: number, width: number, height: number) {
        this.bytes.push(ESC, 0x45)
        this.line(text, align, width, height)
        this.bytes.push(ESC, 0x46)
    }

    lineParts(parts: Array<{ text: string; bold?: boolean }>, align: number, width: number, height: number) {
        this.ensure(align, width, height)
        parts.forEach((part) => {
            if (!part.text) return
            if (part.bold) this.bytes.push(ESC, 0x45)
            this.bytes.push(...encodeWindows1252(part.text))
            if (part.bold) this.bytes.push(ESC, 0x46)
        })
        this.bytes.push(0x0a, 0x0d)
    }

    centered(text: string, width: number, height: number) {
        for (const part of wrapWords(text, columnsFor(width))) this.line(part, 1, width, height)
    }

    pair(left: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const line = left.length + 1 + right.length <= columns
            ? left + ' '.repeat(columns - left.length - right.length) + right
            : `${left.slice(0, columns)}\n${right.slice(-columns).padStart(columns, ' ')}`
        for (const part of line.split('\n')) this.line(part, 0, width, height)
    }

    rule(width: number, height: number) {
        this.line('-'.repeat(columnsFor(width)), 1, width, height)
    }

    item(qty: string, name: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const qtyFieldWidth = Math.min(4, Math.max(2, qty.length + 1))
        const qtyField = `${qty} `.slice(0, qtyFieldWidth).padEnd(qtyFieldWidth, ' ')
        const nameWidth = columns - qtyFieldWidth - (right ? right.length + 1 : 0)
        if (!right || nameWidth < 6) {
            wrapWords(name, Math.max(1, columns - qtyFieldWidth)).forEach((part, index) => {
                this.line(index === 0 ? `${qtyField}${part}` : `${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
            })
            if (right) this.line(right.slice(-columns).padStart(columns, ' '), 0, width, height)
            return
        }
        wrapWords(name, nameWidth).forEach((part, index) => {
            if (index === 0) this.line(`${qtyField}${part.padEnd(nameWidth, ' ')} ${right}`, 0, width, height)
            else this.line(`${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
        })
    }

    qr(data: string) {
        const payload = encodeWindows1252(data)
        const length = payload.length
        this.align = -1
        this.bytes.push(ESC, GS, 0x61, 0x01)
        this.align = 1
        this.bytes.push(ESC, GS, 0x79, 0x53, 0x30, 0x02)
        this.bytes.push(ESC, GS, 0x79, 0x53, 0x32, this.qrSize)
        this.bytes.push(ESC, GS, 0x79, 0x53, 0x31, this.qrError)
        this.bytes.push(ESC, GS, 0x79, 0x44, 0x31, 0x00, length & 0xff, (length >> 8) & 0xff, ...payload)
        this.bytes.push(ESC, GS, 0x79, 0x50)
        this.bytes.push(0x0a)
    }

    finish(_beep?: { enabled?: boolean; count?: number }, feeds?: number): Uint8Array {
        const count = feeds == null ? this.feedBeforeCut : feeds
        for (let i = 0; i < count; i++) this.bytes.push(0x0a)
        // Star Line: 2 feeds the slip out to the cutter, then cuts.
        this.bytes.push(ESC, 0x64, 0x02)
        return Uint8Array.from(this.bytes)
    }
}

/** Same canvas as the ESC/POS image path, wrapped as Star Line graphics. */
export function canvasToStarLine(canvas: HTMLCanvasElement, lineFeeds = 3): Uint8Array {
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not get canvas context')
    const width = canvas.width
    const height = canvas.height
    const { data } = context.getImageData(0, 0, width, height)
    const black = (x: number, y: number) => {
        if (x < 0 || y < 0 || x >= width || y >= height) return 0
        return data[(width * y + x) * 4] < 200 ? 1 : 0
    }

    const parts: Uint8Array[] = [Uint8Array.from([ESC, 0x40, 0x18, ESC, 0x30])]
    const slices = Math.ceil(height / 24)
    for (let s = 0; s < slices; s++) {
        const y = s * 24
        const row = new Uint8Array(width * 3)
        for (let x = 0; x < width; x++) {
            const i = x * 3
            for (let band = 0; band < 3; band++) {
                let value = 0
                for (let bit = 0; bit < 8; bit++) {
                    value |= black(x, y + band * 8 + bit) << (7 - bit)
                }
                row[i + band] = value
            }
        }
        const header = Uint8Array.from([ESC, 0x58, width & 0xff, (width >> 8) & 0xff])
        const slice = new Uint8Array(header.length + row.length + 2)
        slice.set(header, 0)
        slice.set(row, header.length)
        slice[slice.length - 2] = 0x0a
        slice[slice.length - 1] = 0x0d
        parts.push(slice)
    }
    const feeds = new Array(Math.max(0, lineFeeds)).fill(0x0a)
    parts.push(Uint8Array.from([ESC, 0x7a, 0x01, ...feeds, ESC, 0x64, 0x00]))

    const length = parts.reduce((sum, part) => sum + part.length, 0)
    const buffer = new Uint8Array(length)
    let offset = 0
    for (const part of parts) {
        buffer.set(part, offset)
        offset += part.length
    }
    return buffer
}
